// Copyright (c) 2024 by Audere

import { Controller, useFormContext } from "react-hook-form";
import { FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import {
  OptionWithDefinition,
  SelectQuestion,
} from "../features/label/labelTypes";
import React, { useCallback, useEffect, useMemo } from "react";
import { isValidValue, validateQuestion } from "../utils/validateQuestion";

import AdditionalInputField from "./AdditionalInputField";
import i18n from "../../i18n";

interface SelectQuestionComponentProps {
  name: string;
  question: SelectQuestion;
  possibleResponses?: string[];
  displayDefinition?: boolean;
  onChangeEvent: () => void;
}

const SelectQuestionComponent: React.FC<SelectQuestionComponentProps> = ({
  name,
  question,
  possibleResponses,
  displayDefinition,
  onChangeEvent,
}) => {
  const { control, watch, setValue, unregister, register } = useFormContext();
  const response = `${name}.response`;
  const commentName = question.commentRequired
    ? `${name}.requiredComment`
    : `${name}.comment`;

  const watchedResponse = watch(response) as string;
  const watchedComment = watch(commentName) as string;
  const showConditionalField = useMemo(
    () =>
      question.addComment &&
      (question.commentRequired === undefined ||
        watchedResponse === question.commentRequired),
    [question, watchedResponse]
  );

  // Clear and remove conditional field
  useEffect(() => {
    if (validateQuestion(question, { response: watchedResponse })) {
      setValue(commentName, "");
      unregister(commentName);
    }
  }, [
    showConditionalField,
    setValue,
    unregister,
    commentName,
    question,
    watchedResponse,
  ]);

  const resetDependents = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isDependentValue = question.dependents?.values.includes(
      e.target.value
    );
    question.dependents?.questionIds.forEach((dependent) => {
      const response = `${dependent}.response`;
      setValue(response, "");
      if (isDependentValue) {
        register(response);
      } else {
        unregister(response);
      }
    });
  };

  const validateFn = useCallback(
    (value: string) => {
      const isValid = validateQuestion(question, {
        response: value,
        requiredComment: question.commentRequired ? watchedComment : undefined,
      });

      if (isValid) return true; // Question valid
      if (!isValidValue(value)) return false; // Main response invalid
      if (showConditionalField && !isValidValue(watchedComment))
        return "Additional response required"; // Required conditional field invalid
    },
    [question, watchedComment, showConditionalField]
  );

  const errorMessage = validateFn(watchedResponse);
  return (
    <section>
      <Controller
        name={response}
        control={control}
        defaultValue=""
        rules={{ validate: validateFn }}
        render={({ field }) => (
          <>
            <RadioGroup
              aria-label={question.id}
              {...field}
              onChange={(e) => {
                field.onChange(e);
                if (question.dependents?.questionIds.length) {
                  resetDependents(e);
                }
                onChangeEvent();
              }}
            >
              {question.options.map((option) => (
                <FormControlLabel
                  key={option.id}
                  value={option.id}
                  control={<Radio />}
                  label={
                    displayDefinition ? (
                      <>
                        <Typography variant="body1">
                          {(option as OptionWithDefinition).name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          gutterBottom
                        >
                          {(option as OptionWithDefinition).definition}
                        </Typography>
                      </>
                    ) : (
                      option.name
                    )
                  }
                />
              ))}
            </RadioGroup>
            {errorMessage && (
              <Typography variant="caption" color="info">
                {errorMessage === true ? "" : errorMessage}
              </Typography>
            )}
          </>
        )}
      />
      {showConditionalField && (
        <section style={{ marginTop: "1rem" }}>
          <Typography gutterBottom>
            {question.commentLabel ?? i18n.t("questions.defaultCommentLabel")}
          </Typography>
          <AdditionalInputField
            name={commentName}
            inputType={possibleResponses ? "dropdown" : "text"}
            possibleResponses={possibleResponses ?? undefined}
            onChangeEvent={onChangeEvent}
          />
        </section>
      )}
    </section>
  );
};

// Memoized to re-render only when props change
export default React.memo(SelectQuestionComponent);
